import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsent } from '../context/ConsentContext';
import { useLanguage } from '../context/LanguageContext';

export default function CookieConsentManager() {
  const {
    preferences,
    hasDecision,
    isPreferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useConsent();
  const { localizePath, t } = useLanguage();
  const [draftPreferences, setDraftPreferences] = useState(preferences);

  useEffect(() => {
    setDraftPreferences(preferences);
  }, [preferences, isPreferencesOpen]);

  useEffect(() => {
    if (!isPreferencesOpen) {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isPreferencesOpen]);

  const bannerVisible = !hasDecision;

  return (
    <>
      {bannerVisible && (
        <div className="consent-banner" role="dialog" aria-labelledby="cookie-banner-title" aria-modal="false">
          <div className="consent-banner-copy">
            <p className="consent-banner-eyebrow">{t('cookieConsent.bannerEyebrow')}</p>
            <h2 id="cookie-banner-title">{t('cookieConsent.bannerTitle')}</h2>
            <p>{t('cookieConsent.bannerBody')}</p>
            <Link to={localizePath('/privacy')} className="consent-policy-link">
              {t('cookieConsent.privacyLink')}
            </Link>
          </div>
          <div className="consent-banner-actions">
            <button type="button" className="btn-outline consent-secondary-button" onClick={openPreferences}>
              {t('cookieConsent.manage')}
            </button>
            <button type="button" className="btn-outline consent-secondary-button" onClick={rejectNonEssential}>
              {t('cookieConsent.rejectNonEssential')}
            </button>
            <button type="button" className="book-btn consent-primary-button" onClick={acceptAll}>
              {t('cookieConsent.acceptAll')}
            </button>
          </div>
        </div>
      )}

      {isPreferencesOpen && (
        <div className="consent-modal-overlay" role="presentation" onClick={closePreferences}>
          <div
            className="consent-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-preferences-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="consent-modal-header">
              <div>
                <p className="consent-banner-eyebrow">{t('cookieConsent.modalEyebrow')}</p>
                <h2 id="cookie-preferences-title">{t('cookieConsent.modalTitle')}</h2>
              </div>
              <button
                type="button"
                className="consent-close-button"
                onClick={closePreferences}
                aria-label={t('cookieConsent.close')}
              >
                ×
              </button>
            </div>

            <p className="consent-modal-body">{t('cookieConsent.modalBody')}</p>

            <div className="consent-option-list">
              <div className="consent-option-card consent-option-card--locked">
                <div className="consent-option-topline">
                  <div>
                    <h3>{t('cookieConsent.necessaryTitle')}</h3>
                    <p>{t('cookieConsent.necessaryBody')}</p>
                  </div>
                  <span className="consent-status-pill">{t('cookieConsent.alwaysOn')}</span>
                </div>
              </div>

              <label className="consent-option-card" htmlFor="consent-analytics">
                <div className="consent-option-topline">
                  <div>
                    <h3>{t('cookieConsent.analyticsTitle')}</h3>
                    <p>{t('cookieConsent.analyticsBody')}</p>
                  </div>
                  <input
                    id="consent-analytics"
                    type="checkbox"
                    checked={draftPreferences.analytics}
                    onChange={(event) => {
                      setDraftPreferences((current) => ({
                        ...current,
                        analytics: event.target.checked,
                      }));
                    }}
                  />
                </div>
              </label>

              <label className="consent-option-card" htmlFor="consent-external-media">
                <div className="consent-option-topline">
                  <div>
                    <h3>{t('cookieConsent.externalMediaTitle')}</h3>
                    <p>{t('cookieConsent.externalMediaBody')}</p>
                  </div>
                  <input
                    id="consent-external-media"
                    type="checkbox"
                    checked={draftPreferences.externalMedia}
                    onChange={(event) => {
                      setDraftPreferences((current) => ({
                        ...current,
                        externalMedia: event.target.checked,
                      }));
                    }}
                  />
                </div>
              </label>
            </div>

            <div className="consent-modal-actions">
              <button type="button" className="btn-outline consent-secondary-button" onClick={closePreferences}>
                {t('cookieConsent.cancel')}
              </button>
              <button type="button" className="btn-outline consent-secondary-button" onClick={rejectNonEssential}>
                {t('cookieConsent.rejectNonEssential')}
              </button>
              <button
                type="button"
                className="book-btn consent-primary-button"
                onClick={() => savePreferences(draftPreferences)}
              >
                {t('cookieConsent.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
